import { useState } from "react";
import { SEOHead } from "../components/SEOHead";
import { STATIC_PAGES } from "../seo/pages";
import { PublicHeader } from "../components/PublicHeader";
import { CertificateModal, type CertificateAsset } from "../components/CertificateModal";
import "../styles/PublicCommon.css";
import "./AboutPage.css";

// 許可・資格 → static/ の証明書ファイルマッピング（資料がない項目はマップに含めない）
// label は AboutPage の <span className="license-detail-label"> のテキストと一致させる
const LICENSE_ASSETS: Record<string, CertificateAsset> = {
  "NADA石綿調査診断士": {
    title: "NADA石綿調査診断士 登録証",
    url: "/static/NADA%E7%9F%B3%E7%B6%BF%E8%AA%BF%E6%9F%BB%E8%A8%BA%E6%96%AD%E5%A3%AB%E7%99%BB%E9%8C%B2%E8%A8%BC.pdf",
    kind: "pdf",
    caption: "登録番号 NADA8136",
  },
  "特別建築物石綿含有建材調査者": {
    title: "特定建築物石綿含有建材調査者 修了証",
    url: "/static/%E7%89%B9%E5%AE%9A%E5%BB%BA%E7%AF%89%E7%89%A9%E7%9F%B3%E7%B6%BF%E5%90%AB%E6%9C%89%E5%BB%BA%E6%9D%90%E8%AA%BF%E6%9F%BB%E8%80%85239513.docx",
    kind: "docx",
    caption: "終了証明番号 239513",
  },
};

export const AboutPage = () => {
  const [openCert, setOpenCert] = useState<CertificateAsset | null>(null);

  return (
    <>
      <SEOHead page={STATIC_PAGES.about} />

      <div className="public-page">
        <PublicHeader activePage="about" />

        {/* メインコンテンツ */}
        <main id="main-content" role="main">
          {/* ページヘッダー */}
          <section
            className="page-header about-page-header"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(26,158,150,0.78) 0%, rgba(13,120,114,0.78) 50%, rgba(21,138,130,0.78) 100%), url('/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E7%A4%BE%E5%86%85%E7%8E%84%E9%96%A2.JPG')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="page-header-overlay" />
            <div className="page-header-content">
              <p className="page-subtitle-top">Company Profile</p>
              <h1 className="page-title">会社概要</h1>
              <p className="page-description">
                愛媛県西予市を拠点に、地域の皆様の暮らしを支えています
              </p>
            </div>
          </section>

          {/* 企業理念セクション */}
          <section className="philosophy-section" aria-labelledby="philosophy-heading">
            <div className="container">
              <div className="philosophy-inner">
                <div className="philosophy-badge">Philosophy</div>
                <h2 id="philosophy-heading" className="philosophy-main-title">
                  企業理念
                </h2>
                <p className="philosophy-catchphrase">
                  『安心安全な街づくり』
                </p>
                <p className="philosophy-description">
                  私たちは、地域社会の発展と人々の生活基盤の向上に貢献し、<br />
                  未来へつながる安心安全な街づくりを目指します。
                </p>
              </div>
            </div>
          </section>

          {/* 代表挨拶セクション */}
          <section className="message-section" aria-labelledby="message-heading">
            <div className="container">
              <div className="message-content">
                <aside className="message-name-area" aria-label="代表者情報">
                  <div className="message-signet">
                    <span className="message-signet-label">代表取締役</span>
                    <span className="message-signet-name">都築 忍</span>
                    <span className="message-signet-en">President &amp; CEO</span>
                  </div>
                </aside>

                <div className="message-text-area">
                  <div className="message-header">
                    <span className="message-badge">Message</span>
                    <h2 id="message-heading" className="message-title">代表挨拶</h2>
                  </div>

                  <div className="message-body">
                    <p className="message-lead">
                      この度は、都建設株式会社のホームページをご覧いただき、
                      誠にありがとうございます。
                    </p>
                    <p>
                      都建設株式会社は、土木工事、解体工事を中心に日々活動をしております。
                      お客様の立場での現場施工を心がけています。
                    </p>
                    <p>
                      私たちは「安心安全な街づくり」を企業理念とし、
                      地域の皆様に信頼される企業を目指しております。
                      一つひとつの工事に真摯に向き合い、
                      品質と安全を最優先に取り組んでまいります。
                    </p>
                    <p>
                      お客様にとって一番ベストな施工方法をご提案致しますので、
                      ぜひお気軽にお問い合わせください。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 数字で見る都建設 */}
          <section className="numbers-section" aria-labelledby="numbers-heading">
            <div className="container">
              <h2 id="numbers-heading" className="section-title">
                <span className="section-title-en">Numbers</span>
                数字で見る都建設
              </h2>

              <div className="numbers-grid">
                <div className="number-card">
                  <div className="number-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="number-value">
                    <span className="number">10</span>
                    <span className="number-unit">年</span>
                  </div>
                  <p className="number-label">創業からの歴史</p>
                  <p className="number-detail">平成26年創業</p>
                </div>

                <div className="number-card">
                  <div className="number-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                  </div>
                  <div className="number-value">
                    <span className="number">12</span>
                    <span className="number-unit">名</span>
                  </div>
                  <p className="number-label">従業員数</p>
                  <p className="number-detail">経験豊富なスタッフ</p>
                </div>

                <div className="number-card">
                  <div className="number-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                    </svg>
                  </div>
                  <div className="number-value">
                    <span className="number">8</span>
                    <span className="number-unit">種</span>
                  </div>
                  <p className="number-label">建設業許可</p>
                  <p className="number-detail">多彩な工事に対応</p>
                </div>

                <div className="number-card">
                  <div className="number-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="number-value">
                    <span className="number">1.76</span>
                    <span className="number-unit">億円</span>
                  </div>
                  <p className="number-label">年間売上高</p>
                  <p className="number-detail">R8年7月決算</p>
                </div>
              </div>
            </div>
          </section>

          {/* 会社情報セクション */}
          <section className="info-section" aria-labelledby="company-info-heading">
            <div className="container">
              <h2 id="company-info-heading" className="section-title">
                <span className="section-title-en">Company Info</span>
                会社情報
              </h2>

              <div className="company-info-card">
                <table className="info-table">
                  <tbody>
                    <tr>
                      <th>社名</th>
                      <td>都建設株式会社</td>
                    </tr>
                    <tr>
                      <th>所在地</th>
                      <td>
                        <p><strong>本社</strong>：〒797-0015 愛媛県西予市宇和町卯之町1丁目432番地</p>
                        <p><strong>作業所</strong>：〒797-0020 愛媛県西予市宇和町久枝933-1</p>
                      </td>
                    </tr>
                    <tr>
                      <th>連絡先</th>
                      <td>
                        <p>TEL：<a href="tel:0894-62-4511">0894-62-4511</a></p>
                        <p>FAX：0894-62-4568</p>
                      </td>
                    </tr>
                    <tr>
                      <th>設立</th>
                      <td>平成26年10月9日</td>
                    </tr>
                    <tr>
                      <th>資本金</th>
                      <td>100万円</td>
                    </tr>
                    <tr>
                      <th>従業員数</th>
                      <td>12名</td>
                    </tr>
                    <tr>
                      <th>売上高</th>
                      <td>17,619万円（R8年7月決算）</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* 許可・資格セクション */}
          <section className="license-section" aria-labelledby="license-heading">
            <div className="container">
              <h2 id="license-heading" className="section-title section-title-light">
                <span className="section-title-en">License</span>
                許可・資格
              </h2>

              <div className="license-grid">
                <div className="license-card">
                  <div className="license-card-header">
                    <div className="license-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                    <h3>建設業許可</h3>
                  </div>
                  <p className="license-number">愛媛県知事 許可（般-6）第17427号</p>
                  <ul className="license-list">
                    <li>土木工事業</li>
                    <li>どび・土工工事業</li>
                    <li>石工事業</li>
                    <li>鋼構造物工事業</li>
                    <li>舗装工事業</li>
                    <li>しゅんせつ工事業</li>
                    <li>水道施設工事業</li>
                    <li>解体工事業</li>
                  </ul>
                </div>

                <div className="license-card">
                  <div className="license-card-header">
                    <div className="license-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3>その他許可・資格</h3>
                  </div>
                  <ul className="license-detail-list">
                    <li>
                      <span className="license-detail-label">産業廃棄物収集運搬業許可</span>
                      <span className="license-detail-value">許可番号 03813182471</span>
                    </li>
                    {(() => {
                      const cert = LICENSE_ASSETS["NADA石綿調査診断士"];
                      return (
                        <li
                          className="license-clickable"
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpenCert(cert)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setOpenCert(cert);
                            }
                          }}
                          title="クリックで登録証を表示"
                          style={{ cursor: "pointer" }}
                        >
                          <span className="license-detail-label">
                            NADA石綿調査診断士
                            <span className="license-doc-badge" aria-hidden="true"> 📄</span>
                          </span>
                          <span className="license-detail-value">登録番号 NADA8136</span>
                        </li>
                      );
                    })()}
                    {(() => {
                      const cert = LICENSE_ASSETS["特別建築物石綿含有建材調査者"];
                      return (
                        <li
                          className="license-clickable"
                          role="button"
                          tabIndex={0}
                          onClick={() => setOpenCert(cert)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setOpenCert(cert);
                            }
                          }}
                          title="クリックで証明書を表示"
                          style={{ cursor: "pointer" }}
                        >
                          <span className="license-detail-label">
                            特別建築物石綿含有建材調査者
                            <span className="license-doc-badge" aria-hidden="true"> 📄</span>
                          </span>
                          <span className="license-detail-value">終了証明番号 239513</span>
                        </li>
                      );
                    })()}
                    <li>
                      <span className="license-detail-label">工作物石綿事前調査者</span>
                      <span className="license-detail-value">証明番号 2411908</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* 沿革セクション */}
          <section className="history-section" aria-labelledby="history-heading">
            <div className="container">
              <h2 id="history-heading" className="section-title">
                <span className="section-title-en">History</span>
                沿革
              </h2>

              <div className="history-timeline">
                <div className="history-item">
                  <div className="history-year">2014</div>
                  <div className="history-content">
                    <h3>会社設立</h3>
                    <p>
                      愛媛県西予市宇和町にて都建設株式会社を設立。
                      土木工事・解体工事を中心に事業を開始。
                    </p>
                  </div>
                </div>

                <div className="history-item">
                  <div className="history-year">2016</div>
                  <div className="history-content">
                    <h3>建設業許可取得</h3>
                    <p>
                      愛媛県知事より建設業許可を取得。
                      事業拡大に向けた基盤を整備。
                    </p>
                  </div>
                </div>

                <div className="history-item">
                  <div className="history-year">2020</div>
                  <div className="history-content">
                    <h3>事業拡大</h3>
                    <p>
                      ガーデニング工事・造成工事など、
                      サービス領域を拡大。地域密着型の経営を推進。
                    </p>
                  </div>
                </div>

                <div className="history-item">
                  <div className="history-year">2024</div>
                  <div className="history-content">
                    <h3>創業10周年</h3>
                    <p>
                      創業10周年を迎え、さらなる飛躍に向けて
                      設備投資・人材育成を強化。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 事務所の雰囲気セクション */}
          <section className="office-section" aria-labelledby="office-heading">
            <div className="container">
              <h2 id="office-heading" className="section-title">
                <span className="section-title-en">Office</span>
                事務所の雰囲気
              </h2>

              <div className="office-grid">
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E7%A4%BE%E5%86%85%E7%8E%84%E9%96%A2.JPG"
                    alt="社内玄関"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>社内玄関</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E6%89%93%E3%81%A1%E5%90%88%E3%82%8F%E3%81%9B%E5%A0%B4%E6%89%80.JPG"
                    alt="打ち合わせスペース"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>打ち合わせスペース</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E6%9C%9D%E7%A4%BC%E5%A0%B4%E6%89%80.JPG"
                    alt="朝礼場所"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>朝礼場所</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E7%A4%BE%E5%86%85%E3%82%B4%E3%83%AB%E3%83%95%E7%B7%B4%E7%BF%92%E5%A0%B4.JPG"
                    alt="社内ゴルフ練習場"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>社内ゴルフ練習場</h3>
                </div>
              </div>
            </div>
          </section>

          {/* 施設紹介セクション */}
          <section className="office-section" aria-labelledby="facility-heading" style={{ background: "#fff" }}>
            <div className="container">
              <h2 id="facility-heading" className="section-title">
                <span className="section-title-en">Facilities</span>
                施設紹介
              </h2>

              <div className="office-grid">
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E4%BD%9C%E6%A5%AD%E5%A0%B4.JPG"
                    alt="作業場"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>作業場</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E5%80%89%E5%BA%AB%E5%86%85.JPG"
                    alt="倉庫内"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>倉庫内</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E5%80%89%E5%BA%AB%E5%86%85%EF%BC%92.JPG"
                    alt="第二倉庫"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>第二倉庫</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E7%94%A3%E6%A5%AD%E5%BB%83%E6%A3%84%E7%89%A9%E4%BF%9D%E7%AE%A1%E6%89%80.JPG"
                    alt="産業廃棄物保管所"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>産業廃棄物保管所</h3>
                </div>
              </div>
            </div>
          </section>

          {/* 社内行事セクション */}
          <section className="office-section" aria-labelledby="events-heading" style={{ background: "#f8fafa" }}>
            <div className="container">
              <h2 id="events-heading" className="section-title">
                <span className="section-title-en">Events</span>
                社内行事
              </h2>

              <div className="office-grid">
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/12%E6%9C%8829%E6%97%A5%E3%81%8A%E6%AD%A3%E6%9C%88%E6%BA%96%E5%82%99%E8%A1%8C%E4%BA%8B.JPG"
                    alt="12月29日 お正月準備行事"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>お正月準備行事</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E3%81%8A%E9%A3%BE%E3%82%8A.JPG"
                    alt="お飾り"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>お飾り</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/%E3%81%8A%E6%AD%A3%E6%9C%88%E3%81%8A%E8%8A%B1.JPG"
                    alt="お正月のお花"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>お正月のお花</h3>
                </div>
                <div className="office-item">
                  <img
                    src="/static/%E7%A4%BE%E5%86%85%E9%96%A2%E4%BF%82/1%E6%9C%885%E6%97%A5%E4%BB%95%E4%BA%8B%E5%88%9D%E3%82%81.JPG"
                    alt="1月5日 仕事初め"
                    loading="lazy"
                    style={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  <h3>仕事初め</h3>
                </div>
              </div>
            </div>
          </section>

          {/* アクセスセクション */}
          <section className="access-section" aria-labelledby="access-heading">
            <div className="container">
              <h2 id="access-heading" className="section-title">
                <span className="section-title-en">Access</span>
                アクセス
              </h2>

              <div className="access-content">
                <div className="access-info-card">
                  <div className="access-info-header">
                    <div className="access-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <h3>所在地</h3>
                  </div>

                  <address className="access-address">
                    <p className="access-postal">〒797-0015</p>
                    <p className="access-street">愛媛県西予市宇和町卯之町1丁目432番地</p>
                  </address>

                  <div className="access-contact">
                    <p>
                      <span className="access-contact-label">TEL</span>
                      <a href="tel:0894-62-4511">0894-62-4511</a>
                    </p>
                    <p>
                      <span className="access-contact-label">FAX</span>
                      <span>0894-62-4568</span>
                    </p>
                  </div>

                  <div className="access-buttons">
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=愛媛県西予市宇和町卯之町1丁目432番地"
                      className="access-button primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l5.447 2.724A1 1 0 0021 18.618V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      ルート検索
                    </a>
                    <a
                      href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432番地"
                      className="access-button secondary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                      </svg>
                      拡大地図
                    </a>
                  </div>
                </div>

                <div className="access-map">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3323.1!2d132.5!3d33.4!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzPCsDI0JzAwLjAiTiAxMzLCsDMwJzAwLjAiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="都建設株式会社 所在地マップ"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* CTA セクション */}
          <section className="cta-section" aria-labelledby="cta-heading">
            <div className="container">
              <h2 id="cta-heading" className="cta-title">
                お気軽にお問い合わせください
              </h2>
              <p className="cta-description">
                解体工事・土木工事・ガーデニング工事など、<br className="sp-only" />
                まずはご相談からお待ちしております。
              </p>
              <div className="cta-buttons">
                <a href="/contact" className="cta-button primary">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  お問い合わせ
                </a>
                <a href="tel:0894-62-4511" className="cta-button secondary">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  0894-62-4511
                </a>
              </div>
            </div>
          </section>
        </main>

        {/* フッター */}
        <footer className="public-footer" role="contentinfo">
          <div className="footer-content">
            <div className="footer-logo">
              <img
                src="/static/miyako-logo-small.png"
                srcSet="/static/miyako-logo-small.png 1x, /static/miyako-logo-2x.png 2x"
                alt="都建設株式会社"
                className="footer-logo-image"
                width={60}
                height={45}
              />
              <span className="footer-company-name">都建設株式会社</span>
            </div>

            <address className="footer-address-block">
              <a
                href="https://maps.google.com/?q=愛媛県西予市宇和町卯之町1丁目432番地"
                className="footer-address"
                target="_blank"
                rel="noopener noreferrer"
              >
                〒797-0015 愛媛県西予市宇和町卯之町1丁目432番地
              </a>

              <a href="tel:0894-62-4511" className="footer-phone">
                お電話：<span itemProp="telephone">0894-62-4511</span>
              </a>
            </address>

            <nav className="footer-nav" aria-label="フッターナビゲーション">
              <a href="/">ホーム</a>
              <a href="/about">会社概要</a>
              <a href="/services">業務内容</a>
              <a href="/works">施工実績</a>
              <a href="/recruit">採用情報</a>
              <a href="/contact">お問い合わせ</a>
            </nav>

            <p className="footer-copyright">
              <small>
                ©2024 都建設株式会社 Powered by{" "}
                <a href="#" target="_blank" rel="noopener noreferrer">
                  VERYWEBB
                </a>
              </small>
            </p>
          </div>
        </footer>
      </div>

      {/* 許可・資格クリックで開く証明書プレビューモーダル */}
      <CertificateModal asset={openCert} onClose={() => setOpenCert(null)} />
    </>
  );
};

export default AboutPage;
